import React, { useEffect, useState, useContext, Suspense } from "react";
import { NewsContextProvider } from "../contexts/NewsContext";
import { SubscriptionProvider } from "../contexts/SubscriptionContext";
const HomePage = React.lazy(() => import('./components/HomePage'));
const ArticlePage = React.lazy(() => import('./components/ArticlePage')); 
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
const Search = React.lazy(() => import('./components/Search')); 
const CategoryPage = React.lazy(() => import('./components/CategoryPage'));
const EmailConfirmation = React.lazy(() => import('./components/EmailConfirmation'));
const ResetPassword = React.lazy(() => import('./components/ResetPassword')); 
const SubscriptionPage = React.lazy(() => import('./components/SubscriptionPage')); 
const AccountPage = React.lazy(() => import('./components/AccountPage'));
const SuccessPage = React.lazy(() => import('./components/SuccessPage'));  
const NewsNode = React.lazy(() => import('./components/NewsNode'));
const NotFoundPage = React.lazy(() => import('./components/NotFoundPage'));
const DefiData = React.lazy(() => import('./components/DefiData'));
import { ThemeProvider } from '../contexts/ThemeContext';  
const L2Data = React.lazy(() => import('./components/L2Data'));
const NFTData = React.lazy(() => import('./components/NFTData'));
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./components/TermsOfService'));
const ValuesEthics = React.lazy(() => import('./components/ValuesEthics'));
import './components/style.css';
import UrlNormalizer from './components/UrlNormalizer';
const ErrorPage = React.lazy(() => import('./components/ErrorPage'));
import useScrollRestoration from './components/useScrollRestoration';
const CheckoutPage = React.lazy(() => import('./components/CheckoutPage')); 
const GenericMetadata = React.lazy(() => import('./components/GenericMetadata'));
import NavigationTracker from './components/analytics/NavigationTracker';
const AnalyticsDashboard = React.lazy(() => import('./components//analytics/AnalyticsDashboard'));  
const HistoricalAnalytics = React.lazy(() => import('./components//analytics/HistoricalAnalytics'));  
import Layout from "./components/Layout";   
const LoginForm = React.lazy(() => import('./components/LoginForm'));
const RegistrationForm = React.lazy(() => import('./components/RegistrationForm'));
const ForgotPasswordForm = React.lazy(() => import('./components/ForgotPasswordForm'));
const Modal = React.lazy(() => import('./components/Modal'));  
const AdStats = React.lazy(() => import('./components//analytics/AdStats'));  
const BotnewsFrontpage = React.lazy(() => import('./components/botnews/BotnewsFrontpage'));  
const GPTFrontInterface = React.lazy(() => import('./components/botnews/GPTFrontInterface')); 
const BotsDashboard = React.lazy(() => import('./components/botnews/BotsDashboard')); 
const Oyster = React.lazy(() => import('./components/botnews/Oyster'));
const BlockchainStatus = React.lazy(() => import('./components/botnews/BlockchainStatus'));
const MarketLabeler = React.lazy(() => import('./components/botnews/MarketLabeler'));
const Inputs = React.lazy(() => import('./components/botnews/inputs'));
const ArticlesList = React.lazy(() => import('./components/dash/articlesList'));
const ArticleForm = React.lazy(() => import('./components/dash/ArticleForm'));
const BotNewsArticlePage = React.lazy(() => import('./components/BotNewsArticlePage'));



const Newsfeed = () => {
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const q = searchParams.get('q');

    return q ? <Search /> : <HomePage />;
}

const App = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState("");
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const darkmodePref = window.localStorage.getItem('darkMode');
    return darkmodePref ? JSON.parse(darkmodePref) : false;
  });

  useEffect(() => {
    window.localStorage.setItem('darkMode', isDarkMode);
  }, [isDarkMode]);

  const handleModal = (content) => {
    if (content) {
      setModalContent(content);
      setShowModal(true);
    } else {
      setModalContent("");
      setShowModal(false);
    }
  };

  const closeModal = () => {
    setModalContent("");
    setShowModal(false);
    
  }

  const ScrollRestorationWrapper = () => {
    useScrollRestoration(); // Hook is now inside the Router context
    return null; // No UI rendering
  };

  return (
    <ThemeProvider value={{ isDarkMode, setIsDarkMode }}>

    <NewsContextProvider
      value={{
        setSearchQuery,
        searchQuery,
      }}
    >
      <SubscriptionProvider>

        <Router>

          <UrlNormalizer />
          <ScrollRestorationWrapper />
          {/* Include the NavigationTracker to monitor navigation globally */}
          <NavigationTracker />

          <Suspense>
           {modalContent === 'login_metamask' && 
              <MetamaskLoginForm 
                handleModal={handleModal}
                setModalContent={setModalContent}
              />
            }

            {modalContent !== "" && 
              <Modal title={modalContent} onClose={closeModal} isLoading={false}>
                {modalContent === 'login' && 
                  <LoginForm 
                    handleModal={handleModal}
                    setModalContent={setModalContent}
                  />
                } 
                {modalContent === 'register' && <RegistrationForm handleModal={handleModal} setModalContent={setModalContent}/>}

                {modalContent === 'forgot_password' && <ForgotPasswordForm handleModal={handleModal} setModalContent={setModalContent}/>}
              </Modal>
            }
          </Suspense>
                  
          <Routes> 

              <Route
                path="/"
                element={<Layout handleModal={handleModal} modalContent={modalContent} closeModal={closeModal} />} 
              >

            {/* The index route or any nested route will render in the <Outlet /> */}
            <Route index element={<Newsfeed />} />
     
            <Route path="/:year/:month/:day/:title" element={<ArticlePage handleModal={handleModal} />} />  
            <Route path="/news/:categoryName" element={<CategoryPage />} />
            <Route path="/confirmation/:token" element={<EmailConfirmation />} />
            <Route path="/reset_password/:token" element={<ResetPassword />} /> 

            <Route
              path="/subscription"
              element={
                <>
                  <GenericMetadata
                    title="Subscribe to Trustnodes"
                    description="Enjoy unlimited articles and exclusive benefits. Subscribe now."
                    url="https://www.trustnodes.com/subscription"
                  />
                  <SubscriptionPage handleModal={handleModal} />
                </>
              }
            />

            <Route
              path="/account"
              element={
                <>
                  <GenericMetadata
                    title="Account Page - Trustnodes"
                    description="Your account page at Trustnodes."
                    url="https://www.trustnodes.com/account"
                  />
                  <AccountPage />
                </>
              }
            />

             <Route
              path="/checkout"
              element={
                <Suspense fallback={<div>Loading Checkout...</div>}>
                  <>
                    <GenericMetadata
                      title="Checkout Page - Trustnodes"
                      description="Complete your purchase on our secure checkout page."
                      url="https://www.trustnodes.com/checkout"
                    />
                    <CheckoutPage />
                  </>
                </Suspense>
              }
            />
            <Route path="/success" element={<SuccessPage />} />
            <Route path="/newsnode" element={<NewsNode />} />
            
            <Route
              path="/defidata"
              element={
                <>
                  <GenericMetadata
                    title="Defi Stats"
                    description="Top defi dapps by eth holdings."
                    url="https://www.trustnodes.com/defidata"
                  />
                  <DefiData/>
                </>
              }
            />
            <Route
              path="/l2data"
              element={
                <>
                  <GenericMetadata
                    title="L2 Stats"
                    description="Top L2 networks by eth holdings."
                    url="https://www.trustnodes.com/l2data"
                  />
                  <L2Data />
                </>
              }
            />
            <Route
              path="/nftdata"
              element={
                <>
                  <GenericMetadata
                    title="NFT Stats"
                    description="Top NFTs on the ethereum network by market cap and activity."
                    url="https://www.trustnodes.com/nftdata"
                  />
                  <NFTData />
                </>
              }
            />
            <Route
              path="/privacy-policy"
              element={
                <>
                  <GenericMetadata
                    title="Privacy Policy"
                    description="Trustnode's Privacy Policy Page."
                    url="https://www.trustnodes.com/privacy-policy"
                  />
                  <PrivacyPolicy />
                </>
              }
            />
            <Route
              path="/terms-of-service"
              element={
                <>
                  <GenericMetadata
                    title="Terms of Service"
                    description="Trustnode's Terms of Service."
                    url="https://www.trustnodes.com/terms-of-service"
                  />
                  <TermsOfService />
                </>
              }
            />
            <Route
              path="/our-values-and-ethics"
              element={
                <>
                  <GenericMetadata
                    title="Our Values and Ethics"
                    description="Trustnode's Values and Ethics and about us page."
                    url="https://www.trustnodes.com/our-values-and-ethics"
                  />
                  <ValuesEthics />
                </>
              }
            />
            <Route
              path="*"
              element={
                <>
                  <GenericMetadata
                    title="404 Not Found"
                    description="The 404 not found page."
                    url="https://www.trustnodes.com/*"
                  />
                  <NotFoundPage />
                </>
              }
            />

            <Route
              path="/err"
              element={
                <>
                  <GenericMetadata
                    title="500 Error Page"
                    description="500 Error Page, please refresh and try again"
                    url="https://www.trustnodes.com/err"
                  />
                  <ErrorPage />
                </>
              }
            />
            <Route
              path="/analytics"
              element={
                <>
                  <GenericMetadata
                    title="Trustnodes - Analytics"
                    description="Analytics Page"
                    url="https://www.trustnodes.com/analytics"
                  />
                  <AnalyticsDashboard />
                </>
              }
            />
            
            <Route path="/analytic" element={<HistoricalAnalytics />} />

            <Route path="/adstats" element={<AdStats />} />

            <Route path="/botnews" element={<BotnewsFrontpage />} />
            

            <Route path="/botchat" element={<GPTFrontInterface />} />

            <Route path="/botsdashboard" element={<BotsDashboard />} />

            <Route path="/oyster" element={<Oyster />} />

            <Route path="/blockchainstatus" element={<BlockchainStatus />} />
            
            <Route path="/marketlabeler" element={<MarketLabeler />} />
            <Route path="/inputs" element={<Inputs />} />

            <Route path="/articles" element={<ArticlesList />} />
            <Route path="/newArticle" element={<ArticleForm />} />
            <Route path="/editArticle/:id" element={<ArticleForm />} />

            <Route path="/news/:year/:month/:day/:slug" element={<BotNewsArticlePage />} />


          </Route> 

          </Routes>
   
        </Router>
   
      </SubscriptionProvider>  
    </NewsContextProvider>
    </ThemeProvider>
  );
};

export default App;